/**
 * Central factory for React Query keys.
 *
 * All `useQuery` / `useMutation` call sites should source their keys from
 * here instead of inlining string arrays. This is the contract that
 * `queryClient.invalidateQueries(...)` relies on — when two call sites
 * "agree" on a key by typing the same literal, drift is silent and only
 * surfaces as a stale screen weeks later. Centralising the keys turns
 * that into a TypeScript error.
 *
 * Conventions:
 * - Top-level namespace = entity / domain (`customers`, `orders`, ...).
 * - `all()` covers every query for the namespace (use for blanket
 *   invalidation after a mutation).
 * - `lists()` / `details()` are intermediate prefixes — invalidate them
 *   to clear all list / all detail queries for the entity without
 *   touching specific sub-resources.
 * - `list(params)` / `detail(id)` are leaf keys — pass to `useQuery`.
 *
 * The shapes intentionally match the inline keys that existed before
 * this factory landed (`[slug, 'list', params]`, `[slug, 'detail', id]`),
 * so a partial migration does NOT split the cache.
 *
 * For audit, leaf shapes are `[root, 'list'|'resource', ...]` — every
 * shape under a root carries an explicit discriminator so an
 * `invalidateQueries({ queryKey: queryKeys.audit.all() })` sweep is the
 * single correct way to refresh every audit view after a write.
 * (The `reconcile-runs` family used the same shape but was retired in
 * EPIC #503 Sub-PR D alongside the rest of the reconcile UI.)
 */

/**
 * The generic CRUD-resource key factory moved into `@resource-kit/controllers`
 * (#2167, ADR-0035 D1): the four shapes it mints are the resource layer's, and
 * the controllers invalidate against them, and #2170 pointed the host's callers
 * at the package too. The named registry below stays — it enumerates THIS app's
 * entities, which a package cannot know.
 */
import { resourceKeys } from '@resource-kit/controllers';

/**
 * Anything object-shaped (incl. typed interfaces like `DataTableParams`)
 * or `undefined`. We don't constrain to `Record<string, unknown>` because
 * a typed interface without an index signature would be rejected even
 * though it embeds fine as a tuple element — query keys only use the
 * value for structural equality, never key enumeration.
 */
export type ListParams = object | undefined;

/**
 * Single source of truth for the entity-name → slug mapping used by
 * `ResourcePage` definitions. Adding a new CRUD resource means adding
 * one entry here; the named entry on `queryKeys` is then derived
 * automatically (no separate `queryKeys.foo: resourceKeys('foo')` line
 * to keep in sync).
 */
export const RESOURCES = {
  customers: 'customers',
  products: 'products',
  orders: 'orders',
  drivers: 'drivers',
  tasks: 'tasks',
  webhooks: 'webhooks',
  // The dispatch sub-resources use slash-separated slugs to match the
  // backend route paths (`/dispatch/jobs`, `/dispatch/executions`).
  // Keep them as separate top-level entries — TanStack Query's prefix
  // match is positional on tuple elements, so `['dispatch']` is NOT a
  // prefix of `['dispatch/jobs', ...]`. The DispatchDashboard
  // invalidation step lists all three (`dispatch`, `dispatchJobs`,
  // `dispatchExecutions`) explicitly.
  dispatchJobs: 'dispatch/jobs',
  dispatchExecutions: 'dispatch/executions',
  // M2 Menu Planning (issue #156). Slug matches the ResourceDefinition
  // slug in `frontend/src/features/menu-planning/schema.tsx`; lifecycle
  // mutations (approve/publish/unpublish/republish-fanout/amend)
  // invalidate via `queryKeys.menuPlans.detail(id)` after success.
  menuPlans: 'menu-planning-plans',
} as const satisfies Record<string, string>;

export type ResourceName = keyof typeof RESOURCES;

type ResourceKeys = { [K in ResourceName]: ReturnType<typeof resourceKeys<(typeof RESOURCES)[K]>> };

const resourceEntries = Object.fromEntries(
  (Object.entries(RESOURCES) as [ResourceName, (typeof RESOURCES)[ResourceName]][]).map(
    ([name, slug]) => [name, resourceKeys(slug)],
  ),
) as ResourceKeys;

export const queryKeys = {
  ...resourceEntries,

  // Sub-PR F (issue #516): operator review queue inbox. Closed
  // namespace mirroring the backend review-queue API
  // surface. Hierarchy:
  //   all()                  → ['review-queue']                  — blanket invalidation
  //   list(params)           → ['review-queue', 'list', params]
  //   count()                → ['review-queue', 'count']
  //   detail(id)             → ['review-queue', 'detail', id]
  //   byOrder(shopifyOrderId)→ ['review-queue', 'by-order', id]
  // A `resolve` / `bulk-resolve` mutation invalidates `all()` so
  // the inbox + count badge refresh without per-key choreography.
  reviewQueue: {
    all: () => ['review-queue'] as const,
    // Typed `Record<string, unknown>` (not `object | undefined`) so two
    // callers with semantically-equivalent shapes (`{}` vs `{kind: undefined}`)
    // collapse to one cache slot. (The former review-queue list hook that
    // normalised these params was removed in #1416 when the inbox converged
    // onto the shared resource keys, `resourceKeys('review-queue')`; this
    // namespace remains the factory for review-queue-specific cache slots.)
    list: (params: Record<string, unknown>) => ['review-queue', 'list', params] as const,
    count: () => ['review-queue', 'count'] as const,
    // Open items of one kind (#2456 overview inbox card); under `count()` so
    // the same invalidation refreshes it.
    countByKind: (kind: string) => ['review-queue', 'count', 'kind', kind] as const,
    detail: (id: string) => ['review-queue', 'detail', id] as const,
    byOrder: (shopifyOrderId: string) => ['review-queue', 'by-order', shopifyOrderId] as const,
  },

  // Sub-PR G (issue #516): operator inbox for the `webhook_failures`
  // table (ADR-0011 §3 synchronous-handler failure log). Closed
  // namespace mirroring the backend webhook-failures API
  // surface. Hierarchy mirrors the review-queue namespace:
  //   all()                  → ['webhook-failures']                  — blanket invalidation
  //   list(params)           → ['webhook-failures', 'list', params]
  //   count(params)          → ['webhook-failures', 'count', params]
  //   detail(id)             → ['webhook-failures', 'detail', id]
  //   byTrace(traceId)       → ['webhook-failures', 'by-trace', traceId]
  // A `resolve` / `bulk-resolve` mutation invalidates `all()` so the
  // inbox + count badge + open trace views refresh without per-key
  // choreography. The count namespace carries the filter params
  // because the badge endpoint accepts the same filter axes — a
  // future filtered-badge surface would key independently from the
  // global default-arg variant.
  webhookFailures: {
    all: () => ['webhook-failures'] as const,
    list: (params: Record<string, unknown>) => ['webhook-failures', 'list', params] as const,
    count: (params: Record<string, unknown>) => ['webhook-failures', 'count', params] as const,
    detail: (id: string) => ['webhook-failures', 'detail', id] as const,
    byTrace: (traceId: string) => ['webhook-failures', 'by-trace', traceId] as const,
  },

  // Per-topic webhook delivery-health snapshot (#1438). Read-only; the
  // window is part of the key so a window switch re-keys the cache and
  // refetches rather than serving a stale window's snapshot.
  webhookHealth: {
    all: () => ['webhook-health'] as const,
    snapshot: (params: Record<string, unknown>) => ['webhook-health', 'snapshot', params] as const,
  },

  // Tenant admin pages (#2366). The current-tenant keys carry no id: the
  // backend resolves the tenant from the request, so there is one of each.
  tenantIntegrations: {
    all: () => ['tenant-integrations'] as const,
    current: () => ['tenant-integrations', 'current'] as const,
    webhooks: (provider: string) => ['tenant-integrations', 'webhooks', provider] as const,
  },
  tenantSettings: {
    current: () => ['tenant-settings', 'current'] as const,
  },
  // Tenant branding (#2387): read by the sidebar of every member. The logo
  // key carries the versioned logoUrl, so a replaced logo is a new entry.
  tenantBranding: {
    current: () => ['tenant-branding', 'current'] as const,
    logo: (logoUrl: string) => ['tenant-branding', 'logo', logoUrl] as const,
  },

  // Override `orders` to add the per-order trace key under the same
  // root. Sub-PR H consumes the per-order trace endpoint; keying
  // under `['orders', 'trace', id]` means a blanket
  // `invalidate(queryKeys.orders.all())` (e.g. after a Sub-PR I
  // refetch with `status: 'applied'`) also refreshes the open trace
  // view so new `operator_refetch` events appear without a manual
  // browser reload.
  orders: {
    ...resourceEntries.orders,
    // R1 P2 fix: `string` (not `string | undefined`) — the caller is
    // gated by the conditional render upstream, so an undefined id
    // can't reach here. Dropping the `__missing__` sentinel avoids
    // future cache-namespace collision (two unrelated undefined-id
    // call sites would otherwise share the same key).
    trace: (shopifyOrderId: string) => ['orders', 'trace', shopifyOrderId] as const,
    // Per-order fulfillment-job list (the order-detail "structure" tree,
    // #1187). Keyed under the `orders` root so a blanket
    // `invalidate(queryKeys.orders.all())` also refreshes it.
    jobs: (shopifyOrderId: string) => ['orders', 'jobs', shopifyOrderId] as const,
    // Blanket-invalidation prefix for every open per-order trace view.
    // `['orders', 'trace']` is a TanStack prefix of every
    // `trace(id)` leaf, so a resolve/replay mutation can refresh all
    // open traces in one sweep without hand-typing the literal (#1155).
    traces: () => ['orders', 'trace'] as const,
    // The review queue's raw-detail slot — an independent sub-namespace, the
    // same split the `customers` override makes below and for the same reason.
    // `CurrentOrderContents` (`features/admin/pages/reviewQueueResource.tsx`)
    // caches the RAW `{ order, line_items, customer }` `OrderDetail` straight
    // from `GET /api/orders/{id}`, whereas `ResourceDetailPage` caches the
    // FLATTENED `OrderResourceRow` the orders `api.get` adapter builds, under
    // `detail(id)`. Both surfaces read the same endpoint for the same order,
    // so their id spaces overlap.
    //
    // Sharing the plain `detail(id)` key collided, and since the resource
    // contracts left the hgs envelope (#2167 PR-2) the collision is SILENT:
    // the detail page's guard is `!query.data` and nothing anywhere checks the
    // shape, so the raw envelope passes it and the page renders a record with
    // every field undefined — including the object handed to
    // `detail.headerActions`.
    //
    // Keyed as `['orders','raw-detail',id]` — a SEPARATE namespace, not nested
    // under `detail(id)`. Nesting (`[...detail(id),'raw']`, the
    // menuPlans.weekDetail approach) would make the raw slot a prefix-match
    // target of `details()` / `detail(id)` sweeps; disjoint namespaces keep
    // each sweep confined to one shape. Invalidation needs nothing extra:
    // every sweep over an order goes through `all()`
    // (`src/features/orders/hooks/useRefetchFromShopify.ts::useRefetchFromShopify`,
    // and the resource layer's own `keys.all()` —
    // `packages/resource-kit-ui/react/resource/ResourceDetailSurface.tsx::ResourceDetailSurface`,
    // `packages/resource-kit-ui/react/resource/useRowActionRunner.tsx::useRowActionRunner`,
    // `packages/resource-kit-controllers/react/listController.ts::useUrlOwnedList`),
    // and `['orders']` is a TanStack prefix of both namespaces.
    rawDetail: (id: string | number | undefined) => ['orders', 'raw-detail', id] as const,
  },

  // Override `drivers` to add the selector-cache key under the same
  // root. `['drivers']` is a TanStack prefix of `['drivers','selector']`
  // so blanket `invalidate(queryKeys.drivers.all())` after a
  // create/delete already covers the selector — but giving it a
  // discoverable namespace stops future authors hand-typing
  // `['drivers','selector']` and recreating the magic-string contract.
  drivers: {
    ...resourceEntries.drivers,
    selector: () => ['drivers', 'selector'] as const,
  },

  // `customers.detail(id)` caches the FLATTENED `Customer` that
  // `customerResource.host.api.get`
  // (`src/features/customers/schema.tsx::customerResource`)
  // builds; `ResourceDetailPage` reads it under `detail(id)`.
  customers: {
    ...resourceEntries.customers,
  },

  // Menu plans — list + detail keys from the shared resource entries.
  // The planning-time needs aggregation key was removed in the #1151
  // repoint (the rebuilt `/api/menu` face has no needs endpoint).
  menuPlans: {
    ...resourceEntries.menuPlans,
    // The week-grid page caches the raw `{ plan, items }` envelope from
    // `menuPlanAPI.detail`, whereas ResourceDetailPage caches the FLATTENED
    // `MenuPlanWithItems` its `api.get` adapter builds, under the plain
    // `detail(id)` key. Sharing one key collided: within the 5-min staleTime,
    // navigating detail → week → detail read the wrong shape from cache — as
    // "record not found" while the page still unwrapped an envelope, and since
    // #2167 PR-2 (guard `!query.data`, no shape check anywhere) as a record
    // with every field undefined. Distinct keys are what prevent it.
    // Nest the week key UNDER `detail(id)` so it is a distinct cache slot
    // yet still a TanStack prefix-match target of the lifecycle mutations
    // that invalidate `detail(id)` — both views refresh in one sweep.
    weekDetail: (id: string | number | undefined) =>
      [...resourceEntries.menuPlans.detail(id), 'week'] as const,
  },

  // Dispatch.
  dispatch: {
    all: () => ['dispatch'] as const,
    // Tenant delivery flow (#2453): board columns, range and rest days.
    flow: () => ['dispatch', 'flow'] as const,
    // Onfleet assignment progress (#2471). Under the `dispatch` root so a driver import refreshes it.
    assignment: (date: string) => ['dispatch', 'assignment', date] as const,
    // The consolidate=false key is the funnel's read; the dashboard's standing
    // dry-run uses the consolidated key (#2475).
    preview: (date: string, consolidate = false) =>
      consolidate
        ? (['dispatch', 'preview', date, 'consolidated'] as const)
        : (['dispatch', 'preview', date] as const),
    salesBridge: (date: string) => ['dispatch', 'sales-bridge', date] as const,
    // Delivered / late for the last delivery days up to `to` (#2455).
    // One shipping day's outcome row from recent-outcomes (#2484); NOT
    // `recentOutcomes`, whose slot belongs to the overview's 3-day read.
    dayOutcome: (date: string) => ['dispatch', 'day-outcome', date] as const,
    recentOutcomes: (to: string) => ['dispatch', 'recent-outcomes', to] as const,
    // Shared cache slot for the on-demand truth-reconcile report. Single-sourced
    // here so TruthReconcileCard (the owner that fetches it) and SalesBridgeCard
    // (a passive reader that cross-links its unpaid list to financial drift)
    // agree on the key without either hand-typing the literal.
    truthReconcile: (date: string) => ['dispatch', 'truth-reconcile', date] as const,
    eta: () => ['dispatch', 'eta'] as const,
    // The date's drivers on the road and job totals behind the en-route pane (#2483).
    enRoute: (date: string) => ['dispatch', 'en-route', date] as const,
    // The date's printout data behind the awaiting-departure pane (#2480).
    departure: (date: string) => ['dispatch', 'departure', date] as const,
    // The date's exception / cancelled jobs behind that pane (#2481), under the
    // `dispatch` root so a cancel's `dispatch.all()` invalidation refreshes it.
    exceptionOrCancelled: (date: string) => ['dispatch', 'exception-or-cancelled', date] as const,
    // Batch board window (#2438), under the `dispatch` root so a run's
    // `dispatch.all()` invalidation refreshes it.
    board: (from: string, to: string) => ['dispatch', 'board', from, to] as const,
    // The undated-order pool (#2561): one pool for every day, so no date in
    // the key. Under the `dispatch` root so a schedule's `dispatch.all()`
    // invalidation refreshes it with the day's preview and count check.
    undatedOrders: () => ['dispatch', 'undated-orders'] as const,
  },

  // Exceptions.
  exceptions: {
    all: () => ['exceptions'] as const,
    list: (statusFilter: unknown, severityFilter: unknown, typeFilter: unknown, page: number) =>
      ['exceptions', 'list', statusFilter, severityFilter, typeFilter, page] as const,
    counts: () => ['exceptions', 'counts'] as const,
    cutoff: (date: string) => ['exceptions', 'cutoff', date] as const,
  },

  // Packaging.
  packaging: {
    all: () => ['packaging'] as const,
    list: (date: string) => ['packaging', date] as const,
  },

  // Audit logs. The `'list'` and `'resource'` discriminators keep the
  // two query shapes structurally distinct so a future contract test
  // can grep for them, and so an `invalidateQueries(['audit-logs'])`
  // sweep is the only correct way to refresh both views after a write.
  audit: {
    all: () => ['audit-logs'] as const,
    list: (params: ListParams) => ['audit-logs', 'list', params] as const,
    forResource: (resourceType: string, resourceId: string) =>
      ['audit-logs', 'resource', resourceType, resourceId] as const,
    // A SEPARATE key from `forResource`: the Shopify lookup is opt-in and
    // costs an outbound Admin call, so it must not be invalidated (or
    // refetched) whenever the ledger page it annotates is.
    shopifyActors: (resourceType: string, resourceId: string) =>
      ['audit-logs', 'shopify-actors', resourceType, resourceId] as const,
  },

  // Reconciliation runs query keys retired in EPIC #503 Sub-PR D —
  // the reconcile-runs backend endpoints + frontend UI were
  // deleted alongside `modules/reconcile/` per ADR-0007.

  // M4 Inventory (issue #428). Snapshot key discriminates on the
  // canonical-sorted (trim+dedup) id list so two pages requesting
  // overlapping subsets share neither cache slots nor stale data
  // (different requested-id sets are different queries). The
  // history-drilldown key will land alongside the history-UI hook
  // in a future PR — not added speculatively (EQD §7).
  inventory: {
    all: () => ['inventory'] as const,
    onHand: (ingredientId: string) => ['inventory', 'on-hand', ingredientId] as const,
  },

  // M3 Procurement (issue #611 — M3-T6 frontend + routes). Closed
  // namespace mirroring the backend `/admin/procurement/*` surface.
  // `all()` is the blanket-invalidation prefix; mutations on
  // POs / suggestions invalidate `all()` to refresh every list +
  // detail in one sweep (the lists carry small N today; finer-grain
  // invalidation is a perf optimisation tracked as a follow-up).
  procurement: {
    all: () => ['procurement'] as const,
    suggestions: () => ['procurement', 'suggestions'] as const,
    suggestionsList: (params: object | undefined) =>
      ['procurement', 'suggestions', 'list', params] as const,
    purchaseOrders: () => ['procurement', 'purchase-orders'] as const,
    purchaseOrderDetail: (id: string | undefined) =>
      ['procurement', 'purchase-orders', 'detail', id] as const,
  },

  // M5 Stocktake (issue #616 — M5-T4 frontend + routes). Closed
  // namespace mirroring the backend `/admin/stocktake/*` surface.
  // `all()` is the blanket-invalidation prefix; mutations on a
  // stocktake (open / count / commit / cancel) invalidate `all()`
  // so the list + detail refresh in one sweep. List cardinality is
  // small (one stocktake per week → ~52/year) so finer-grain
  // invalidation is a perf optimisation not worth the complexity.
  stocktake: {
    all: () => ['stocktake'] as const,
    list: (params: object | undefined) => ['stocktake', 'list', params] as const,
    detail: (id: string | undefined) => ['stocktake', 'detail', id] as const,
  },

  // Users management (#1138). Closed namespace for the admin
  // user-management page. `all()` is the blanket-invalidation prefix;
  // `list()` is the non-paginated admin list (the backend caps at 200,
  // so there are no list params to discriminate on), and
  // `permissions(id)` is the per-user granted-permission set read by the
  // RoleEditor. A successful invite / role change invalidates `all()`,
  // which is a TanStack prefix of both `list()` and `permissions(id)`,
  // so the list and any open editor refresh in one sweep.
  users: {
    all: () => ['users'] as const,
    list: () => ['users', 'list'] as const,
    permissions: (id: string) => ['users', 'permissions', id] as const,
  },

  // Top-bar command palette (#2477): one entry per result group.
  globalSearch: {
    orders: (term: string) => ['global-search', 'orders', term] as const,
    jobs: (term: string) => ['global-search', 'jobs', term] as const,
    tasks: (term: string) => ['global-search', 'tasks', term] as const,
  },

  // Admin webhooks health (covers both generic + Shopify-scoped variants).
  adminWebhooks: {
    health: () => ['admin', 'webhooks', 'health'] as const,
    onfleetStatus: () => ['admin', 'webhooks', 'onfleet', 'status'] as const,
    shopifyHealth: (windowHours: number) =>
      ['admin', 'webhooks', 'shopify', 'health', windowHours] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
